import { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Header gradient avec ring SVG de réussite globale + barres par question.
 * Affiché en haut de la page ResultatsExercice.
 */
const ResultatsHeader = memo(function ResultatsHeader({ session, statsParQuestion, statsParEleve, elevesNonRepondus, tauxGlobal, idclasse, isEquipe }) {
  const isEnLigne = session?.mode_passation === 'en_ligne';

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 px-4 sm:px-6 py-5 sm:py-6 rounded-2xl">
      {/* Orbes animées */}
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <radialGradient id="qcm-res-blob1" cx="20%" cy="30%" r="40%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <radialGradient id="qcm-res-blob2" cx="80%" cy="70%" r="35%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.08)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>
        <ellipse cx="20%" cy="30%" rx="180" ry="120" fill="url(#qcm-res-blob1)">
          <animate attributeName="cx" values="20%;30%;20%" dur="8s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="80%" cy="70%" rx="160" ry="100" fill="url(#qcm-res-blob2)">
          <animate attributeName="cx" values="80%;70%;80%" dur="9s" repeatCount="indefinite" />
        </ellipse>
      </svg>

      <div className="relative z-10">
        {/* Top bar — retour + info */}
        <div className="flex items-center justify-between mb-4">
          {/* Retour vers la liste unifiée Évaluations (l'historique QCM
              local a été consolidé dedans) */}
          <Link
            to={`/home/evaluations/${idclasse}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500/80 hover:bg-orange-500 backdrop-blur-md border border-orange-400/30 text-white text-sm font-medium transition shadow-sm"
          >
            <ArrowLeft size={16} />
            Retour
          </Link>
          <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[11px] font-semibold text-white/60 tracking-wider uppercase">
            {isEnLigne ? 'En ligne' : 'ArUco'} · {statsParEleve.length}/{statsParEleve.length + (elevesNonRepondus?.length || 0)}
          </div>
        </div>

        {/* Titre */}
        <div className="mb-1">
          <div className="text-[11px] font-bold text-white/40 uppercase tracking-widest mb-1">
            {isEnLigne ? 'Quizz en ligne' : 'QCM ArUco'} · {session?.matiere || ''}
          </div>
          <h1 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
            {session?.titre || 'Résultats'}
          </h1>
          <p className="text-xs text-white/40 mt-0.5">
            {statsParEleve.length} {isEquipe ? 'équipe' : 'élève'}{statsParEleve.length !== 1 ? 's' : ''} · {statsParQuestion.length} question{statsParQuestion.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Ring + barres par question */}
        <div className="flex items-center gap-5 sm:gap-7 mt-4">
          {/* Ring SVG */}
          <div className="relative flex-shrink-0" style={{ width: 90, height: 90 }}>
            <svg width="90" height="90" viewBox="0 0 100 100" className="-rotate-90">
              <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
              <circle
                cx="50" cy="50" r="40" fill="none"
                stroke="url(#ringGrad)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={251}
                strokeDashoffset={251 - (tauxGlobal / 100) * 251}
                className="transition-all duration-1000"
              />
              <defs>
                <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f87171" />
                  <stop offset="50%" stopColor="#fbbf24" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xl font-black text-white">{tauxGlobal}<span className="text-xs font-semibold text-white/50">%</span></span>
            </div>
          </div>

          {/* Barres par question — compact flex-wrap */}
          <div className="flex-1 flex flex-wrap gap-2 min-w-0">
            {statsParQuestion.map(q => {
              const percent = q.taux;
              const color = percent >= 70 ? 'text-emerald-300 border-emerald-400/30' : percent >= 40 ? 'text-amber-300 border-amber-400/30' : 'text-red-300 border-red-400/30';
              return (
                <div key={q.question_ordre} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/5 border ${color}`}>
                  <span className="text-[11px] font-bold text-white/60">Q{q.question_ordre}</span>
                  <div className="w-12 h-1 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${percent >= 70 ? 'bg-emerald-400' : percent >= 40 ? 'bg-amber-400' : 'bg-red-400'} transition-all duration-700`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-bold font-mono">{q.bonnes}/{q.total}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ResultatsHeader;
